import * as React from 'react';
import { Dialog, DialogContent, Button, Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Lightweight Google Maps loader (standalone from MapPickerModal)
async function loadGoogleMaps(apiKey) {
  if (typeof window !== 'undefined' && window.google && window.google.maps) return window.google.maps;
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('No window'));
    const existing = document.querySelector('script[data-ef-gmaps]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google.maps));
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.setAttribute('data-ef-gmaps', '1');
    script.onload = () => resolve(window.google.maps);
    script.onerror = (e) => reject(e);
    document.head.appendChild(script);
  });
}

function isValidLat(n){ return typeof n==='number' && !Number.isNaN(n) && n>=-90 && n<=90; }
function isValidLng(n){ return typeof n==='number' && !Number.isNaN(n) && n>=-180 && n<=180; }

export default function MapViewModal({ open, onClose, title, address, lat, lng, mapsUrl, gKey }) {
  const mapRef = React.useRef(null);
  const mapObjRef = React.useRef(null);
  const markerRef = React.useRef(null);

  const hasCoords = isValidLat(Number(lat)) && isValidLng(Number(lng));
  const displayTitle = title || 'Location';
  const displayAddress = address || '';

  React.useEffect(() => {
    if (!open || !hasCoords || !gKey) return;
    let cancelled = false;
    (async () => {
      try {
        const gmaps = await loadGoogleMaps(gKey);
        if (cancelled || !mapRef.current) return;
        const center = { lat: Number(lat), lng: Number(lng) };
        const map = new gmaps.Map(mapRef.current, {
          center,
          zoom: 17,
          mapTypeId: 'roadmap',
          disableDefaultUI: true,
          gestureHandling: 'greedy',
        });
        mapObjRef.current = map;
        const marker = new gmaps.Marker({ position: center, map, title: displayTitle });
        markerRef.current = marker;
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [open, hasCoords, gKey, lat, lng, displayTitle]);

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        backdrop: { sx: { backdropFilter: 'blur(2px)' } },
      }}
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(255,255,255,0.20)',
          borderRadius: 3,
          boxShadow: '0 10px 28px rgba(0,0,0,0.16)',
          overflow: 'hidden',
        },
      }}
   >
      <DialogContent dividers={false} sx={{ p: 0 }}>
        <Box sx={{ position: 'relative', width: '100%', height: 480 }}>
          <Box ref={mapRef} sx={{ position: 'absolute', inset: 0 }} />

          {/* Close button (top-right) */}
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ position: 'absolute', right: 10, top: 10, bgcolor: 'rgba(255,255,255,0.85)', boxShadow: 1, '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {/* Title & address (top-left) */}
          <Box sx={{ position: 'absolute', left: 12, top: 12, right: 80, pointerEvents: 'none' }}>
            <Box sx={{ display: 'inline-block', maxWidth: '100%', px: 1.25, py: 0.75, borderRadius: 2, background: 'rgba(255,255,255,0.85)', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                {displayTitle}
              </Typography>
              {!!displayAddress && (
                <Typography variant="body2" sx={{ color: '#4B5563' }} noWrap>
                  {displayAddress}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Open in Google Maps (bottom-right) */}
          {mapsUrl && (
            <Box sx={{ position: 'absolute', right: 12, bottom: 12 }}>
              <Button
                onClick={() => window.open(mapsUrl, '_blank', 'noopener')}
                size="small"
                variant="outlined"
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 2,
                  textTransform: 'none',
                  backdropFilter: 'blur(8px)',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  borderColor: 'rgba(0,0,0,0.12)',
                }}
              >
                Open in Google Maps
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
